import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { FieldState, FormState } from 'formstate';

import {
    InputFieldState,
    TextAreaFieldState,
    formStateToJS,
} from '@servicetitan/form/dist/form-helpers';
import { inject, injectable } from '@servicetitan/react-ioc';

import { WidgetData as WidgetDataApi, Text2ChatWidgetLoadingDataDTO } from '../api/widget-data.api';

import { AnalyticsStore } from './analytics.store';

import {
    maxLengthValidation,
    phoneValidation,
    requiredWithCustomText,
} from '../utils/field-custom-validations';
import { getWidgetInstanceId } from '../utils/get-widget-instance-id';
import { postMessageToParent } from '../utils/post-message-to-parent';
import { setAxiosDefaults } from '../utils/set-axios-defaults';

import { Device } from '../enums/device';
import { isDevelopmentMode } from '../utils/is-development-mode';
import { logger } from '../utils/console';

export const MESSAGE_MAX_LENGTH = 150;

export const NAME_MAX_LENGTH = 70;

/* eslint-disable @typescript-eslint/consistent-type-definitions */
type ContactFormState = {
    phoneNumber: InputFieldState<string>;
    message: TextAreaFieldState<string>;
    name: InputFieldState<string>;
};

@injectable()
export class WidgetStore {
    @observable widgetData?: Text2ChatWidgetLoadingDataDTO;

    @observable pageSizes = { width: 0, height: 0 };

    @observable widgetInstanceId?: string;

    @observable isSubmitting = false;

    @observable isSubmitted = false;

    @observable showWidget = false;

    @observable isLoading = true;

    @observable device?: Device;

    @observable formState: FormState<ContactFormState> = new FormState({
        name: new InputFieldState<string>('').validators(
            requiredWithCustomText('Name is required'),
            maxLengthValidation(NAME_MAX_LENGTH)
        ),
        phoneNumber: new InputFieldState<string>('').validators(
            requiredWithCustomText('Phone Number is required'),
            phoneValidation('Phone Number is not valid')
        ),
        message: new TextAreaFieldState<string>('').validators(
            requiredWithCustomText('Message is required'),
            maxLengthValidation(MESSAGE_MAX_LENGTH)
        ),
    });

    @computed get submitButtonDisabled() {
        return (
            !this.formState.$.phoneNumber.value ||
            !this.formState.$.message.value ||
            !this.formState.$.name.value ||
            this.isSubmitting ||
            this.formState.hasError
        );
    }

    constructor(
        @inject(AnalyticsStore) private analyticsStore: AnalyticsStore,
        @inject(WidgetDataApi) private widgetDataApi: WidgetDataApi
    ) {
        makeObservable(this);
    }

    @action setPageSizes = (pageSizes: { width: number; height: number }) => {
        this.pageSizes = pageSizes;
    };

    @action setWidgetInstanceId = (widgetInstanceId: string) => {
        this.widgetInstanceId = widgetInstanceId;
    };

    @action setShowWidget = (showWidget: boolean) => {
        this.showWidget = showWidget;
    };

    @action setIsLoading = (isLoading: boolean) => {
        this.isLoading = isLoading;
    };

    @action setSubmitting = (isSubmitting: boolean) => {
        this.isSubmitting = isSubmitting;
    };

    @action setSubmitted = (isSubmitted: boolean) => {
        this.isSubmitted = isSubmitted;
    };

    @action setWidgetData = (widgetData: Text2ChatWidgetLoadingDataDTO) => {
        this.widgetData = widgetData;
    };

    @action setDevice = (device: Device) => {
        this.device = device;
    };

    init = () => {
        this.analyticsStore.init();

        if (window.self === window.top) {
            this.analyticsStore.widgetLoadedOutsideOfIframe();
            logger.error('Wrong loading option');
            return;
        }

        const widgetInstanceId = getWidgetInstanceId();

        setAxiosDefaults(widgetInstanceId);

        if (!widgetInstanceId) {
            return;
        }

        this.setWidgetInstanceId(widgetInstanceId);

        this.widgetDataApi
            .getWidgetData(widgetInstanceId)
            .then(({ data: widgetData }) => {
                this.setWidgetData(widgetData);
            })
            .catch()
            .finally(() => {
                if (!this.widgetData?.websiteUrl) {
                    this.analyticsStore.widgetLoadedInWrongWebsite(
                        new URL(document.referrer).hostname
                    );
                    logger.error('Website url is not exist!');
                    return;
                }

                let preventAccessToWidget = false;
                const websiteHostname = this.getUrlHostname(this.widgetData?.websiteUrl);
                const parentDomain = this.getUrlHostname(document.referrer);

                if (websiteHostname) {
                    preventAccessToWidget = websiteHostname !== parentDomain;
                } else {
                    preventAccessToWidget = !this.isSiteContainsDomain(
                        parentDomain!,
                        this.removeTrailingSlash(this.widgetData?.websiteUrl)
                    );
                }

                if (!isDevelopmentMode()) {
                    if (preventAccessToWidget) {
                        this.analyticsStore.widgetLoadedInWrongWebsite(
                            new URL(document.referrer).hostname
                        );
                        logger.error('Wrong parent page!');
                        return;
                    }
                }

                this.setIsLoading(false);

                postMessageToParent({ type: 'ready' });

                this.catchValidation(this.formState.$.phoneNumber, 'phoneNumber');
                this.catchValidation(this.formState.$.message, 'message');
                this.catchValidation(this.formState.$.name, 'name');
            });
    };

    catchValidation = (fieldState: FieldState<any>, fieldName: string) => {
        reaction(
            () => fieldState.error,
            error => {
                if (error) {
                    this.analyticsStore.trackValidationError(fieldName, error);
                }
            }
        );
    };

    openWidget = () => {
        const {
            $: { phoneNumber, message, name },
        } = this.formState;

        this.analyticsStore.widgetOpened();

        runInAction(() => {
            this.formState = new FormState({
                name: new InputFieldState<string>(this.isSubmitted ? '' : name.value).validators(
                    requiredWithCustomText('Name is required'),
                    maxLengthValidation(NAME_MAX_LENGTH)
                ),
                phoneNumber: new InputFieldState<string>(
                    this.isSubmitted ? '' : phoneNumber.value
                ).validators(
                    requiredWithCustomText('Phone Number is required'),
                    phoneValidation('Phone Number is not valid')
                ),
                message: new TextAreaFieldState<string>(
                    this.isSubmitted ? '' : message.value
                ).validators(
                    requiredWithCustomText('Message is required'),
                    maxLengthValidation(MESSAGE_MAX_LENGTH)
                ),
            });
        });

        this.setSubmitted(false);

        if (this.device === Device.Mobile) {
            postMessageToParent({
                type: 'setFrameSizes',
                height: '100%',
                width: '100%',
            });
        } else {
            postMessageToParent({
                type: 'setFrameSizes',
                height: '800px',
                width: '420px',
            });
        }

        this.setShowWidget(true);
    };

    closeWidget = () => {
        this.analyticsStore.widgetClosed();

        postMessageToParent({
            type: 'setFrameSizes',
            height: '84px',
            width: '84px',
        });

        this.setShowWidget(false);
    };

    handleSubmit = async () => {
        const { hasError } = await this.formState.validate();

        if (!hasError && this.widgetInstanceId) {
            this.setSubmitting(true);

            const { name, phoneNumber, message } = formStateToJS(this.formState);

            const data = {
                publicToken: this.widgetInstanceId,
                mobilePhoneNumber: phoneNumber,
                message: message.trim(),
                name: name.trim(),
            };

            this.analyticsStore.sendingMessage(data);

            try {
                await this.widgetDataApi.submitHomeOwnerDetails(data);

                this.analyticsStore.messageSent();
                this.setSubmitting(false);
                this.setSubmitted(true);
            } catch (e) {
                // do nothing yet
            }
        }
    };

    private getUrlHostname = (targetUrl: string) => {
        try {
            const hostname = new URL(targetUrl).hostname;
            const pattern = 'www.';

            if (hostname.indexOf(pattern) === 0) {
                return hostname.replace(pattern, '');
            }

            return hostname;
        } catch (_) {
            return undefined;
        }
    };

    private isSiteContainsDomain = (parentDomain: string, webSiteDomain: string) => {
        const pattern = 'www.';
        let targetStr = webSiteDomain;
        if (webSiteDomain.indexOf(pattern) === 0) {
            targetStr = webSiteDomain.replace(pattern, '');
        }

        return parentDomain.includes(targetStr);
    };

    private removeTrailingSlash = (str: string) => {
        return str.replace(/\/+$/, '');
    };
}
